import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { subscribeMyFiles, uploadMyFile, deleteMyFile, getMyFileDownloadUrl } from '../../services/myFilesService';
import { ArrowLeft, Plus, Upload, Trash2, Download, FileText } from 'lucide-react';

const MAX_FILE_SIZE_MB = 10;

function MyFilesPage({ onBack }) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeMyFiles(currentUser.uid, setFiles);
  }, [currentUser?.uid]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser?.uid) return;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(t('myFiles.fileTooBig') || `File must be under ${MAX_FILE_SIZE_MB} MB`);
      return;
    }
    setError('');
    setUploading(true);
    try {
      await uploadMyFile(currentUser.uid, file);
    } catch (err) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId, fileRef) => {
    if (!currentUser?.uid || !window.confirm(t('myFiles.deleteConfirm'))) return;
    try {
      await deleteMyFile(currentUser.uid, fileId, fileRef);
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const handleDownload = async (fileRef) => {
    try {
      const url = await getMyFileDownloadUrl(fileRef.storagePath);
      window.open(url, '_blank');
    } catch (err) {
      setError(err?.message || 'Download failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 self-start"
          >
            <ArrowLeft size={20} />
            {t('back')}
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{t('myFiles.title')}</h1>
          <label className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer text-sm font-medium self-stretch sm:self-auto">
            <Upload className="w-4 h-4" />
            {uploading ? t('myFiles.uploading') : t('myFiles.upload')}
            <input
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
              disabled={uploading}
              onChange={handleUpload}
            />
          </label>
        </div>

        <p className="text-gray-600 mb-4 text-sm">{t('myFiles.hint')}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {files.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>{t('myFiles.noFiles')}</p>
            <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
              <Plus size={18} />
              {t('myFiles.upload')}
              <input type="file" className="hidden" accept=".pdf,.doc,.docx" disabled={uploading} onChange={handleUpload} />
            </label>
          </div>
        ) : (
          <ul className="space-y-2">
            {files.map((file) => (
              <li
                key={file.id}
                className="flex items-center justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <span className="text-gray-800 truncate flex-1" title={file.name}>
                  {file.label || file.name}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleDownload(file)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title={t('myFiles.download')}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(file.id, file)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title={t('myFiles.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default MyFilesPage;
